/**
 * Centralized pricing and identifier utility for Combo Meals and item replacements.
 */

export const MENU_TYPE_TO_ID = {
    drink: 1,
    drinks: 1,
    standard_food: 2,
    standard_foods: 2,
    food: 2,
    dessert: 3,
    desserts: 3,
    tea_coffee: 4,
    tea_and_coffee: 4,
    coffee: 4,
    pizza: 5,
    pizzas: 5,
    burger: 6,
    burgers: 6,
    kebab: 7,
    kebabs: 7,
    fish_and_chips: 8,
    fish_chips: 8,
    combo_meal: 9,
    combo: 9,
    course: 12,
    courses: 12,
    buffet: 14,
    banquet: 16,
    create_your_own_general: 21,
    create_your_own: 21,
    createyourowngeneral: 21
};

/**
 * Resolves the numeric menu_item_id from item or type.
 */
export const resolveMenuItemId = (item, fallbackType = null) => {
    if (item?.menu_item_id) return Number(item.menu_item_id);
    if (item?.menu_id) return Number(item.menu_id);
    if (item?.menu_item?.menu_item_id) return Number(item.menu_item.menu_item_id);
    const rawType = item?.item_type || item?.menu_type || fallbackType || "";
    const type = String(rawType).toLowerCase().replace(/[\s-]+/g, "_");
    return MENU_TYPE_TO_ID[type] || null;
};

const isValidPositivePrice = (val) => {
    return val !== null && val !== undefined && val !== "" && !isNaN(val) && Number(val) > 0;
};

/**
 * Resolves the original IN-price for a combo item.
 * Rule:
 * 1. Checks original_in_price / in_price
 * 2. If missing or 0/null, falls back to original_price / price / final_price ("kono item a in price na thakle price dea calculate hobe")
 * 3. Falls back to out_price if available
 */
export const resolveOriginalInPrice = (item) => {
    if (!item) return 0;
    const inCandidates = [
        item.original_in_price,
        item.original_item?.original_in_price,
        item.original_item?.in_price,
        item.in_price,
        item.variant?.in_price,
        item.menu_item?.in_price,
    ];
    for (const v of inCandidates) {
        if (isValidPositivePrice(v)) return Number(v);
    }

    const priceCandidates = [
        item.original_price,
        item.original_item?.original_price,
        item.original_item?.price,
        item.price,
        item.final_price,
        item.variant?.price,
        item.menu_item?.price,
    ];
    for (const v of priceCandidates) {
        if (isValidPositivePrice(v)) return Number(v);
    }

    const outCandidates = [
        item.original_out_price,
        item.original_item?.original_out_price,
        item.original_item?.out_price,
        item.out_price,
        item.variant?.out_price,
        item.menu_item?.out_price,
    ];
    for (const v of outCandidates) {
        if (isValidPositivePrice(v)) return Number(v);
    }

    return 0;
};

/**
 * Resolves the original OUT-price for a combo item.
 * Rule:
 * 1. Checks original_out_price / out_price
 * 2. If missing or 0/null, falls back to original_price / price / final_price ("out-price na thakle price dea calculate hobe")
 * 3. Falls back to in_price if available
 */
export const resolveOriginalOutPrice = (item) => {
    if (!item) return 0;
    const outCandidates = [
        item.original_out_price,
        item.original_item?.original_out_price,
        item.original_item?.out_price,
        item.out_price,
        item.variant?.out_price,
        item.menu_item?.out_price,
    ];
    for (const v of outCandidates) {
        if (isValidPositivePrice(v)) return Number(v);
    }

    const priceCandidates = [
        item.original_price,
        item.original_item?.original_price,
        item.original_item?.price,
        item.price,
        item.final_price,
        item.variant?.price,
        item.menu_item?.price,
    ];
    for (const v of priceCandidates) {
        if (isValidPositivePrice(v)) return Number(v);
    }

    const inCandidates = [
        item.original_in_price,
        item.original_item?.original_in_price,
        item.original_item?.in_price,
        item.in_price,
        item.variant?.in_price,
        item.menu_item?.in_price,
    ];
    for (const v of inCandidates) {
        if (isValidPositivePrice(v)) return Number(v);
    }

    return 0;
};

/**
 * Resolves the base original price for a combo item.
 */
export const resolveOriginalPrice = (item) => {
    if (!item) return 0;
    const priceCandidates = [
        item.original_price,
        item.original_item?.original_price,
        item.original_item?.price,
        item.price,
        item.final_price,
        item.original_in_price,
        item.original_out_price,
    ];
    for (const v of priceCandidates) {
        if (isValidPositivePrice(v)) return Number(v);
    }
    return 0;
};

/**
 * Resolves effective IN-price for a new / replacement item.
 * Rule: "table a sudu in-price hobe ... kono item a in price na thakle price dea calculate hobe"
 */
export const resolveNewInPrice = (item, variant = null) => {
    if (isValidPositivePrice(variant?.in_price)) return Number(variant.in_price);
    if (isValidPositivePrice(item?.in_price)) return Number(item.in_price);

    // If in-price not available, fallback to price
    if (isValidPositivePrice(variant?.price)) return Number(variant.price);
    if (isValidPositivePrice(item?.price)) return Number(item.price);

    // Secondary fallback to out-price
    if (isValidPositivePrice(variant?.out_price)) return Number(variant.out_price);
    if (isValidPositivePrice(item?.out_price)) return Number(item.out_price);

    return 0;
};

/**
 * Resolves effective OUT-price for a new / replacement item.
 * Rule: "r table bade sob type a out-price hobe ... out-price na thakle price dea calculate hobe"
 */
export const resolveNewOutPrice = (item, variant = null) => {
    if (isValidPositivePrice(variant?.out_price)) return Number(variant.out_price);
    if (isValidPositivePrice(item?.out_price)) return Number(item.out_price);

    // If out-price not available, fallback to price
    if (isValidPositivePrice(variant?.price)) return Number(variant.price);
    if (isValidPositivePrice(item?.price)) return Number(item.price);

    // Secondary fallback to in-price
    if (isValidPositivePrice(variant?.in_price)) return Number(variant.in_price);
    if (isValidPositivePrice(item?.in_price)) return Number(item.in_price);

    return 0;
};

/**
 * Returns original item price based on active order type.
 */
export const getActiveOriginalPrice = (comboItem, isTable = false) => {
    return isTable ? resolveOriginalInPrice(comboItem) : resolveOriginalOutPrice(comboItem);
};

/**
 * Returns replacement item price based on active order type.
 */
export const getActiveNewPrice = (item, variant = null, isTable = false) => {
    return isTable ? resolveNewInPrice(item, variant) : resolveNewOutPrice(item, variant);
};

/**
 * Calculates complete price differences for replacement.
 */
export const calculateReplacementPriceDiff = (comboItem, selectedItem, selectedVariant, isTable = false) => {
    const originalInPrice = resolveOriginalInPrice(comboItem);
    const originalOutPrice = resolveOriginalOutPrice(comboItem);
    const newInPrice = resolveNewInPrice(selectedItem, selectedVariant);
    const newOutPrice = resolveNewOutPrice(selectedItem, selectedVariant);

    const inDiff = Math.max(0, newInPrice - originalInPrice);
    const outDiff = Math.max(0, newOutPrice - originalOutPrice);
    const appliedDiff = isTable ? inDiff : outDiff;

    return {
        originalInPrice,
        originalOutPrice,
        newInPrice,
        newOutPrice,
        inDiff,
        outDiff,
        appliedDiff
    };
};
